import { FC, useMemo } from 'react';
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  HStack,
  Spacer,
  useDisclosure,
} from '@chakra-ui/react';

import { TaxTypeEnum } from '__generated__/GQL';
import type { Variant as TextVariant } from 'theme/components/Text';

import { formatAmount } from '../../../../../../common/formatAmount';
import Button from '../../../../../../components/_core/Button';
import SunshineIcon from '../../../../../../components/_core/SunshineIcon';
import Text from '../../../../../../components/_core/Text';
import { formatPeriodDate } from '../../common/utils';
import { useTaxForecastPeriod } from '../../hooks/useTaxForecastPeriod';
import locales from '../../locales';
import TaxStatus from '../TaxStatus';
import MarkAsNotPaidButton from './components/MarkAsNotPaidButton';
import MarkAsPaidModal from './components/MarkAsPaidModal';
import RevenueRow from './components/RevenueRow';
import TaxRow from './components/TaxRow';

interface EstimationDetailsProps {
  isOverview?: boolean;
  isCurrentPeriod?: boolean;
  taxForecastPeriodId: string;
  includeIncomeTaxInTotal?: boolean;
  hasBorder?: boolean;
  isSubjectToVAT?: boolean;
}

const EstimationDetails: FC<EstimationDetailsProps> = ({
  hasBorder = false,
  includeIncomeTaxInTotal = false,
  isCurrentPeriod,
  isOverview = false,
  isSubjectToVAT,
  taxForecastPeriodId,
}) => {
  const { taxForecastPeriod } = useTaxForecastPeriod(taxForecastPeriodId);
  const {
    isOpen: isMarkAsPaidModalOpen,
    onClose: onCloseMarkAsPaidModal,
    onOpen: onOpenMarkAsPaidModal,
  } = useDisclosure();
  const radius = isOverview ? 'radius-8' : 'radius-0';

  const computedTotalAmount = useMemo(() => {
    if (!taxForecastPeriod) {
      return 0;
    }

    return taxForecastPeriod.computedTaxForecasts.reduce((sum, taxForecast) => {
      if (
        taxForecast.taxType === TaxTypeEnum.PrelevLib &&
        !includeIncomeTaxInTotal
      ) {
        return sum;
      }

      return sum + taxForecast.amount;
    }, 0);
  }, [taxForecastPeriod, includeIncomeTaxInTotal]);

  if (!taxForecastPeriod) {
    return null;
  }

  const findRateByType = (taxType: TaxTypeEnum) =>
    taxForecastPeriod.computedTaxForecasts.find(
      (forecast) => forecast?.taxType === taxType,
    );

  const periodLabel = formatPeriodDate(
    taxForecastPeriod.periodStartsAt,
    taxForecastPeriod.periodEndsAt,
  );

  const titleRowColor: TextVariant = taxForecastPeriod.isPaid
    ? 'light'
    : 'primary';

  {
    /**
     * TODO(SUN-19): Switch to an element of the design system
     */
  }
  return (
    <>
      <AccordionItem
        backgroundColor={isOverview ? 'grey.100' : 'white'}
        borderBottomLeftRadius={radius}
        borderBottomRightRadius={radius}
        borderBottomWidth={hasBorder ? 1 : 0}
      >
        {({ isExpanded }) => (
          <>
            <h2>
              <AccordionButton
                _expanded={{
                  borderBottomColor: 'grey.300',
                  borderBottomWidth: '1px',
                }}
                _hover={{ bg: 'grey.200' }}
                padding="space-16"
              >
                {isOverview ? (
                  <Text fontWeight="weight-500" variant="primary">
                    {locales.taxDetails.accordionTitle}
                  </Text>
                ) : (
                  <>
                    <HStack gap="space-24">
                      <Text variant={titleRowColor}>{periodLabel}</Text>

                      <TaxStatus
                        isPaid={taxForecastPeriod.isPaid}
                        payBefore={taxForecastPeriod.payBefore}
                      />
                    </HStack>

                    <Spacer />

                    <Text
                      fontWeight="weight-500"
                      marginRight="space-16"
                      variant={titleRowColor}
                    >
                      {formatAmount({
                        forcePlusSign: false,
                        value: taxForecastPeriod.isPaid
                          ? taxForecastPeriod.amountPaid ?? 0
                          : computedTotalAmount,
                      })}
                    </Text>
                  </>
                )}

                <SunshineIcon
                  color="light"
                  name="chevron-down"
                  size="icon-24"
                  transform={isExpanded ? 'rotate(180deg)' : ''}
                  transition="all .25s ease-out"
                />
              </AccordionButton>
            </h2>

            <AccordionPanel
              backgroundColor="white"
              borderBottomLeftRadius={radius}
              borderBottomRightRadius={radius}
              gap="space-32"
              p="space-16"
            >
              {!taxForecastPeriod.isPaid && !isCurrentPeriod ? (
                <Button
                  alignSelf="flex-end"
                  icon="tick"
                  iconPosition="left"
                  onClick={() => onOpenMarkAsPaidModal()}
                  variant="discreet"
                >
                  {locales.taxDetails.ctaPaid}
                </Button>
              ) : null}

              {taxForecastPeriod.isPaid && !isCurrentPeriod ? (
                <MarkAsNotPaidButton
                  taxForecastPeriodId={taxForecastPeriodId}
                />
              ) : null}

              {taxForecastPeriod.isPaid ? (
                <TaxRow
                  amount={taxForecastPeriod.amountPaid}
                  description={locales.taxDetails.amountPaid}
                />
              ) : null}

              {isOverview ? null : (
                <RevenueRow
                  complementaryRevenue={
                    findRateByType(TaxTypeEnum.Rsi)?.complementaryRevenue ?? 0
                  }
                  computedRevenue={
                    findRateByType(TaxTypeEnum.Rsi)?.computedRevenue ?? 0
                  }
                  isSubjectToVAT={Boolean(isSubjectToVAT)}
                  shineRevenue={findRateByType(TaxTypeEnum.Rsi)?.revenue ?? 0}
                  vatAmount={findRateByType(TaxTypeEnum.Rsi)?.vatAmount ?? 0}
                />
              )}

              {findRateByType(TaxTypeEnum.Rsi)?.amount !== 0 ? (
                <TaxRow
                  amount={findRateByType(TaxTypeEnum.Rsi)?.amount}
                  description={locales.taxDetails.rate.RSI}
                  isAmountGrey={taxForecastPeriod.isPaid}
                  isAmountStrikeThrough={taxForecastPeriod.isPaid}
                  isTextGrey={taxForecastPeriod.isPaid}
                />
              ) : null}

              <TaxRow
                amount={findRateByType(TaxTypeEnum.FraisChambre)?.amount}
                description={locales.taxDetails.rate.FRAIS_CHAMBRE}
                isAmountGrey={taxForecastPeriod.isPaid}
                isAmountStrikeThrough={taxForecastPeriod.isPaid}
                isTextGrey={taxForecastPeriod.isPaid}
              />

              <TaxRow
                amount={findRateByType(TaxTypeEnum.Cfp)?.amount}
                description={locales.taxDetails.rate.CFP}
                isAmountGrey={taxForecastPeriod.isPaid}
                isAmountStrikeThrough={taxForecastPeriod.isPaid}
                isTextGrey={taxForecastPeriod.isPaid}
              />

              {!isOverview ? (
                <TaxRow
                  amount={findRateByType(TaxTypeEnum.PrelevLib)?.amount}
                  description={locales.taxDetails.rate.PRELEV_LIB}
                  isAmountGrey={true}
                  isAmountStrikeThrough={
                    taxForecastPeriod.isPaid && includeIncomeTaxInTotal
                  }
                  isTextGrey={
                    taxForecastPeriod.isPaid && includeIncomeTaxInTotal
                  }
                />
              ) : null}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>

      <MarkAsPaidModal
        amount={computedTotalAmount}
        isOpen={isMarkAsPaidModalOpen}
        onClose={onCloseMarkAsPaidModal}
        periodLabel={periodLabel}
        taxForecastPeriodId={taxForecastPeriodId}
      />
    </>
  );
};

export default EstimationDetails;
